// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Click-to-define glossary. `.glossary-link[data-term]` spans anywhere in the UI
 * pop a small definition tooltip; Escape or an outside click dismisses it.
 */

export const GLOSSARY: Record<string, string> = {
  'burn-after-read':
    'The secret is shown to the recipient once, then wiped from the sender’s device and the connection torn down. Nothing is left behind on either side, and nothing was ever stored on a server.',
  'end-to-end encrypted':
    'The secret is scrambled on the sending device and only unscrambled on the receiving device. Nothing in between — no relay, no server — can read it.',
  'data channel':
    'A direct WebRTC pipe between the two browsers. The secret travels over it device-to-device, encrypted on the wire, with no server in the path.',
  webrtc:
    'The browser technology for real-time connections directly between two devices — here used to hand a secret straight from one browser to another.',
  dtls:
    'The encryption WebRTC mandates for every peer-to-peer channel. Your secret is encrypted on the wire between the two browsers.',
  'aes-gcm':
    'A strong authenticated cipher. Burnwell wraps the secret’s bytes in AES-GCM-256 using a key derived from your passphrase, on top of the channel’s own encryption.',
  'signalling relay':
    'A public service used once, at the start, to help two browsers find each other. It only shepherds the encrypted handshake and never carries your secret.',
  stun:
    'A public helper that tells your browser how it appears to the internet, so two devices behind home routers can find a direct path to each other.',
  'symmetric nat':
    'A stricter type of home/office router. If both devices are behind one, browsers sometimes can’t find a direct path without a relay — which we deliberately don’t run.',
  sas:
    'Short Authentication String — four words derived from your shared passphrase. If both devices show the same four words, no impostor slipped into the middle.',
  passphrase:
    'The shared secret for the hand-off. It encrypts the handshake and derives the secret’s key and the four verification words. It lives only in the link’s hash — or, in split mode, is never transmitted at all.',
  'split mode':
    'The link carries only the room id; you read the four-word passphrase to the recipient separately (aloud, or over a different channel). Even someone who grabs the link can’t open the secret without the passphrase.',
  'one-time delivery':
    'The sender releases the secret to exactly one recipient — the first to verify and request it — then burns it. A second device that opens the same link afterwards gets nothing.',
};

let tip: HTMLElement | null = null;

export function initGlossary(root: HTMLElement = document.body): void {
  root.addEventListener('click', (e) => {
    const target = (e.target as HTMLElement).closest('.glossary-link') as HTMLElement | null;
    if (!target) {
      hide();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const term = target.dataset.term?.toLowerCase() ?? '';
    const def = GLOSSARY[term];
    if (!def) return;
    show(target, term, def);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') hide();
  });
  window.addEventListener('scroll', hide, true);
}

function show(anchor: HTMLElement, term: string, def: string): void {
  hide();
  tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.innerHTML = `<div class="glossary-term"></div><div class="glossary-def"></div>`;
  (tip.querySelector('.glossary-term') as HTMLElement).textContent = term;
  (tip.querySelector('.glossary-def') as HTMLElement).textContent = def;
  document.body.appendChild(tip);
  const r = anchor.getBoundingClientRect();
  const tr = tip.getBoundingClientRect();
  let left = r.left;
  if (left + tr.width > window.innerWidth - 12) left = window.innerWidth - tr.width - 12;
  if (left < 12) left = 12;
  let top = r.bottom + 8;
  if (top + tr.height > window.innerHeight - 12) top = r.top - tr.height - 8;
  tip.style.left = `${left}px`;
  tip.style.top = `${Math.max(12, top)}px`;
}

function hide(): void {
  if (tip) {
    tip.remove();
    tip = null;
  }
}
